import { HubFeature } from '@ats/api/cm';
import { LocDefs, LocMap, MapLocKeys } from '@cm/core/localization';

const dpRoot = 'Candidates|Summary|Details';

export type SPCandidateDetailsPageLocs =
	| 'SPCandidateDetailsPagePasswordSectionTitle'
	| 'SPCandidateDetailsPageEditPasswordButton'
	| 'SPCandidateDetailsPageUpdatePasswordButton'
	| 'SPCandidateDetailsPageUpdatePasswordCancelButton'
	| 'SPCandidateDetailsPagePasswordLabel'
	| 'SPCandidateDetailsPageUpdatePasswordResetShowPassword'
	| 'SPCandidateDetailsPageUpdatePasswordResetHidePassword'
	| 'SPCandidateDetailsPageUpdatePasswordRequiredError'
	| 'SPCandidateDetailsPageUpdatePasswordConfirmationRequiredError'
	| 'SPCandidateDetailsPageUpdatePasswordMinCharactersError'
	| 'SPCandidateDetailsPageUpdatePasswordMaxCharactersError'
	| 'SPCandidateDetailsPageUpdatePasswordNoMatchError'
	| 'SPCandidateDetailsPageConfirmPasswordLabel'
	| 'SPCandidateDetailsPasswordUpdateConfirmation'
	| 'SPCandidateDetailsPagePasswordAriaLabel'
	| 'SPCandidateDetailsPageLabelDateOfBirth';

export const spCandidateDetailsPageLocs: LocDefs<SPCandidateDetailsPageLocs> = {
	SPCandidateDetailsPageUpdatePasswordMinCharactersError: {
		defaultValue: 'Password must contain at least 12 characters',
		description: 'Error shown when password is less than 12 characters',
		displayPath: `${dpRoot}|Password Minimum Characters Error`,
		displayType: 'Error',
		portal: 'SP',
		feature: HubFeature.CandidateAccessLoginChangePassword,
	},
	SPCandidateDetailsPasswordUpdateConfirmation: {
		defaultValue: 'Password updated',
		description: 'Notification text  when password is successfully updated',
		displayPath: `${dpRoot}|Password Update Success Notification`,
		displayType: 'Notification',
		portal: 'SP',
		feature: HubFeature.CandidateAccessLoginChangePassword,
	},
	SPCandidateDetailsPageUpdatePasswordMaxCharactersError: {
		defaultValue: 'Password cannot contain more than 50 characters',
		description: 'Error shown when password is more than 50 characters',
		displayPath: `${dpRoot}|Password Maximum Characters Error`,
		displayType: 'Error',
		portal: 'SP',
		feature: HubFeature.CandidateAccessLoginChangePassword,
	},
	SPCandidateDetailsPageUpdatePasswordNoMatchError: {
		defaultValue: 'Passwords do not match',
		description: 'Error shown when password and password confirmation do not match',
		displayPath: `${dpRoot}|Password Match Error`,
		displayType: 'Error',
		portal: 'SP',
		feature: HubFeature.CandidateAccessLoginChangePassword,
	},
	SPCandidateDetailsPageUpdatePasswordRequiredError: {
		defaultValue: 'Password is required',
		description: 'Error shown when password is not provided',
		displayPath: `${dpRoot}|Password Required Error`,
		displayType: 'Error',
		portal: 'SP',
		feature: HubFeature.CandidateAccessLoginChangePassword,
	},
	SPCandidateDetailsPageUpdatePasswordConfirmationRequiredError: {
		defaultValue: 'Password confirmation is required',
		description: 'Error shown when password confirmation is not provided',
		displayPath: `${dpRoot}|Password Required Error`,
		displayType: 'Error',
		portal: 'SP',
		feature: HubFeature.CandidateAccessLoginChangePassword,
	},
	SPCandidateDetailsPagePasswordSectionTitle: {
		defaultValue: 'Password',
		description: 'Title for the password section',
		displayPath: `${dpRoot}|Password Section Title`,
		displayType: 'Title',
		portal: 'SP',
		feature: HubFeature.CandidateAccessLoginChangePassword,
	},
	SPCandidateDetailsPageEditPasswordButton: {
		defaultValue: 'Edit Password',
		description: 'Button to edit password',
		displayPath: `${dpRoot}|Edit Password Button`,
		displayType: 'Button',
		portal: 'SP',
		feature: HubFeature.CandidateAccessLoginChangePassword,
	},
	SPCandidateDetailsPageUpdatePasswordButton: {
		defaultValue: 'Update Password',
		description: 'Button to update password',
		displayPath: `${dpRoot}|Update Password Button`,
		displayType: 'Button',
		portal: 'SP',
		feature: HubFeature.CandidateAccessLoginChangePassword,
	},
	SPCandidateDetailsPageUpdatePasswordCancelButton: {
		defaultValue: 'Cancel',
		description: 'Button to cancel editing password',
		displayPath: `${dpRoot}|Cancel Password update`,
		displayType: 'Button',
		portal: 'SP',
		feature: HubFeature.CandidateAccessLoginChangePassword,
	},
	SPCandidateDetailsPagePasswordLabel: {
		defaultValue: 'Password',
		description: 'Label for password field',
		displayPath: `${dpRoot}|Password Label`,
		displayType: 'Label',
		portal: 'SP',
		feature: HubFeature.CandidateAccessLoginChangePassword,
	},
	SPCandidateDetailsPagePasswordAriaLabel: {
		defaultValue: 'Encoded password',
		description: 'Accessibility label for password field shown as symbols',
		displayPath: `${dpRoot}|Password Aria Label`,
		displayType: 'Label',
		portal: 'SP',
		feature: HubFeature.CandidateAccessLoginChangePassword,
	},
	SPCandidateDetailsPageConfirmPasswordLabel: {
		defaultValue: 'Confirm Password',
		description: 'Label for confirm password field',
		displayPath: `${dpRoot}|Confirm Password Label`,
		displayType: 'Label',
		portal: 'SP',
		feature: HubFeature.CandidateAccessLoginChangePassword,
	},
	SPCandidateDetailsPageUpdatePasswordResetShowPassword: {
		defaultValue: 'Show Password',
		description: 'Button to show password',
		displayPath: `${dpRoot}|Password Show Button`,
		displayType: 'Button',
		portal: 'SP',
		feature: HubFeature.CandidateAccessLoginChangePassword,
	},
	SPCandidateDetailsPageUpdatePasswordResetHidePassword: {
		defaultValue: 'Hide Password',
		description: 'Button to hide password',
		displayPath: `${dpRoot}|Password Hide Button`,
		displayType: 'Button',
		portal: 'SP',
		feature: HubFeature.CandidateAccessLoginChangePassword,
	},
	SPCandidateDetailsPageLabelDateOfBirth: {
		defaultValue: 'Date of Birth',
		description: 'Label for Date of Birth',
		displayPath: `${dpRoot}|Date of Birth`,
		displayType: 'Label',
		portal: 'SP',
		legacyName: 'Core/Candidates.Details.FieldLabel.DOB',
		feature: HubFeature.CandidateProfileDateOfBirth,
		htmlAllowed: false,
	},
};

export const LOC_KEYS = MapLocKeys<SPCandidateDetailsPageLocs>(spCandidateDetailsPageLocs);
export type SPCandidateDetailsFormLocType = LocMap<SPCandidateDetailsPageLocs>;
